/**
 * Hand-built SVG stacked-area chart. No chart library (none is a
 * dependency of this app).
 *
 * The backend's `/ai/usage/timeseries` gives one aggregate point per day
 * (`{ day, tokens_in, tokens_out, cache_read_tokens, requests, cost_usd }`)
 * -- there is no per-provider breakdown at day granularity, so this does
 * NOT stack "by upstream" the way the mockup did. Instead:
 *   - Tokens view stacks the three real components of a day's traffic
 *     (input / output / cached tokens) -- this is "everything", local
 *     models included, because token counts are never null.
 *   - Cost view is a single series of `cost_usd`. Local-model usage is
 *     excluded from it structurally (the backend never counts it into
 *     `cost_usd`), not by a client-side filter -- there is no local/cloud
 *     flag on a timeseries point to filter by. What this component must
 *     still guard is never presenting a day (or the range total) that
 *     has `cost_usd === null` as if it were `$0.00`.
 */

import { useState, type ReactElement } from 'react';
import type { TimeseriesPoint } from '@services/aiUsageService';
import { formatCost, formatMoney, formatTokens, sumNullableCost } from './usageFormat';
import {
  formatDay, gridlines, niceMax, plotGeometry, stackSeries, type ChartSeries,
} from './chartMath';

const PALETTE = ['#3987e5', '#d95926', '#199e70', '#c98500'] as const;

type Metric = 'cost' | 'tokens';

function buildSeries(points: TimeseriesPoint[], metric: Metric): ChartSeries[] {
  if (metric === 'tokens') {
    const tokensIn = points.map((p) => p.tokens_in);
    const tokensOut = points.map((p) => p.tokens_out);
    // Both cache tiers, priced very differently and both real traffic.
    // An agentic client sends almost all of its input as a cache read or
    // a cache write, so a chart missing them is a chart of nothing.
    const cacheRead = points.map((p) => p.cache_read_tokens);
    const cacheWrite = points.map((p) => p.cache_write_tokens);
    return [
      { key: 'tokens_in', label: 'Input tokens', color: PALETTE[0], values: tokensIn, raw: tokensIn },
      { key: 'tokens_out', label: 'Output tokens', color: PALETTE[1], values: tokensOut, raw: tokensOut },
      { key: 'cache_read_tokens', label: 'Cache read', color: PALETTE[2], values: cacheRead, raw: cacheRead },
      { key: 'cache_write_tokens', label: 'Cache write', color: PALETTE[3], values: cacheWrite, raw: cacheWrite },
    ];
  }
  const raw = points.map((p) => p.cost_usd);
  return [
    {
      key: 'cost_usd',
      label: 'Cost (USD)',
      color: PALETTE[0],
      // A zero-height stack segment for a $0 day is correct geometry -- an
      // SVG path needs a number, and a costless day really is 0 dollars.
      // The tooltip and legend never read from this array; they read
      // `raw`, which keeps the true `null`.
      values: raw.map((v) => v ?? 0),
      raw,
    },
  ];
}

interface UsageChartProps {
  points: TimeseriesPoint[];
}

export function UsageChart({ points }: UsageChartProps): ReactElement {
  const [metric, setMetric] = useState<Metric>('cost');
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const series = buildSeries(points, metric);
  const bands = stackSeries(series);
  const totals =
    metric === 'cost'
      ? [sumNullableCost(points.map((p) => p.cost_usd))]
      : series.map((s) => s.values.reduce((a, v) => a + v, 0));

  const W = 900;
  const H = 260;
  // Wide enough for a sub-cent axis label ("$0.000306"); spend at this
  // scale is real and the axis has to be able to say so.
  const L = 76;
  const Rm = 12;
  const T = 12;
  const B = 28;
  const pw = W - L - Rm;
  const ph = H - T - B;
  const n = points.length;
  const stackedMax = Math.max(0, ...bands.map((b) => Math.max(...b.upper, 0)));
  const top = niceMax(stackedMax);
  const plot = plotGeometry(n, L, pw);
  const y = (v: number): number => T + ph - (v / top) * ph;

  // Accepts the nullable `raw` shape so a local-only day/total is never
  // silently coalesced to 0 before formatting.
  const format = (v: number | null | undefined): string =>
    metric === 'cost' ? formatCost(v ?? null) : formatTokens(v ?? 0);

  return (
    <div>
      <div className="usage-chart-head">
        <h3>{metric === 'cost' ? 'Spend over time' : 'Tokens over time'}</h3>
        <div className="usage-seg" role="group" aria-label="Chart metric">
          <button type="button" aria-pressed={metric === 'cost'} onClick={() => { setMetric('cost'); }}>
            Cost
          </button>
          <button type="button" aria-pressed={metric === 'tokens'} onClick={() => { setMetric('tokens'); }}>
            Tokens
          </button>
        </div>
      </div>

      {points.length === 0 ? (
        <p className="usage-chart__empty">No usage in this range.</p>
      ) : (
        <div className="usage-chart-wrap">
          <svg
            className="usage-chart"
            viewBox={`0 0 ${String(W)} ${String(H)}`}
            role="img"
            aria-label="AI usage over time"
          >
            {gridlines(top).map((g) => (
              <g key={g}>
                <line x1={L} y1={y(g)} x2={W - Rm} y2={y(g)} stroke="var(--border-subtle)" strokeWidth={1} />
                <text x={L - 8} y={y(g) + 4} fill="var(--text-muted)" fontSize={11} textAnchor="end">
                  {/* Never Math.round here: whole-dollar labels turn every
                      gridline of a sub-dollar range into "$0". */}
                  {metric === 'cost' ? formatMoney(g) : formatTokens(g)}
                </text>
              </g>
            ))}
            {bands.map((band) => {
              const top_ = plot.xs.map(
                (px, i) => `${String(px)},${String(y(plot.valueAt(band.upper, i)))}`
              );
              const bottom = plot.xs
                .map((px, i) => `${String(px)},${String(y(plot.valueAt(band.lower, i)))}`)
                .reverse();
              return (
                <g key={band.series.key}>
                  <polygon points={[...top_, ...bottom].join(' ')} fill={band.series.color} fillOpacity={0.55} />
                  <polyline points={top_.join(' ')} fill="none" stroke={band.series.color} strokeWidth={2} />
                </g>
              );
            })}
            {hoverIndex !== null && (
              <line
                x1={plot.centreX(hoverIndex)} y1={T}
                x2={plot.centreX(hoverIndex)} y2={T + ph}
                stroke="var(--text-muted)" strokeWidth={1}
              />
            )}
            {points.map((p, i) => (
              <rect
                key={p.day}
                x={plot.hoverX(i)}
                y={T}
                width={plot.bandWidth}
                height={ph}
                fill="transparent"
                onMouseEnter={() => { setHoverIndex(i); }}
                onMouseLeave={() => { setHoverIndex(null); }}
              />
            ))}
          </svg>
          {(() => {
            if (hoverIndex === null) return null;
            const hoverPoint = points[hoverIndex];
            if (!hoverPoint) return null;
            return (
              <div className="usage-chart__tooltip" role="status">
                <div className="usage-chart__tooltip-date">{formatDay(hoverPoint.day)}</div>
                {series.map((s) => (
                  <div className="usage-chart__tooltip-row" key={s.key}>
                    <i className="usage-swatch" style={{ background: s.color }} />
                    {s.label}
                    <span>{format(s.raw[hoverIndex])}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      <div className="usage-legend">
        {series.map((s, i) => (
          <span key={s.key}>
            <i className="usage-swatch" style={{ background: s.color }} />
            {s.label}
            <span className="usage-legend__total">{format(totals[i])}</span>
          </span>
        ))}
      </div>
      <p className="usage-chart__note">
        {metric === 'cost'
          ? 'Local models have no cost and are excluded from this total by design.'
          : 'All usage, including local models -- local traffic consumes tokens, not dollars.'}
      </p>
    </div>
  );
}
